import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Models } from '@ibexfusion/models';
import { StorageService } from '@services/storage/storage.service';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import moment from 'moment';

import { DataProvider } from '@services/data-provider';
import { MessengerService } from '@services/messenger/messenger.service';
import { MatSelect } from '@angular/material/select';

export interface DataTableEvent {
  index: number;
  row: any;
  checked?: boolean;
}

@Component({
    selector: 'data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
    standalone: false
})
export class DataTableComponent implements OnInit, OnChanges, OnDestroy {

  @Input() columns: any[];
  @Input() plainData: any;
  @Input() dataProvider: DataProvider<Models.IbexBaseModel>;
  @Input() dataProviderParams: any = {};
  @Input() identifier = '';
  @Input() title: string;
  @Input() hideSearch: boolean;
  @Input() noDataMessage = 'general.noEntriesFound';
  @Input() validator;
  @Input() table_id: any = false;
  @Input() perPage = false;
  @Input() bigTable = false;
  @Input() icon: string;
  @Input() alternative_icon: string;
  @Input() limit = 10;
  @Input() lastActiveChip: string[] = null;
  @Input() selectable = false;
  @Input() searchColumns: any[];
  @Input() checkBoxActions: any[];
  @Input() searchAll = false;
  @Input() noCard = false;
  @Input() noFooter = false;
  @Input() saveFilters = true;
  @Input() noHeader = false;
  @Input() newHeader = false;
  @Input() headerButtons: {icon: string, callback: any, tooltipTranslation?: string, disabled?: boolean}[] = [];

  @Output() detail: EventEmitter<DataTableEvent> = new EventEmitter();
  @Output() edit: EventEmitter<DataTableEvent> = new EventEmitter();
  @Output() remove: EventEmitter<DataTableEvent> = new EventEmitter();
  @Output() custom: EventEmitter<DataTableEvent> = new EventEmitter();
  @Output() select: EventEmitter<DataTableEvent> = new EventEmitter();
  @Output() add: EventEmitter<DataTableEvent> = new EventEmitter();
  @Output() check: EventEmitter<DataTableEvent> = new EventEmitter();

  public selectedIdList = [];
  public checkedIdList = [];
  public types: any;
  public showDetail = false;
  public showEdit = false;
  public showRemove = false;
  public showCustom = false;
  public showAdd = false;
  public showCheck = false;

  public rows: any[];

  public skip = 0;
  public total = 0;

  public isLoading = false;

  public searchColumn: string;

  public searchValue: string;

  public location: string;

  classProps: any;

  plainOrder: 'desc' | 'asc' = 'asc';

  public navigation = { isFirst: false, isLast: true };
  selectedItems = [];
  private searchTimeout: number;

  constructor(private storage: StorageService, private router: Router, private ms: MessengerService) {
  }

  getActionWidth() {
    return [this.showCustom, this.showDetail, this.showEdit, this.showRemove, this.showCheck, this.selectable].filter(el => !!el)?.length;
  }

  async ngOnInit() {
    for (const col of (this.columns || []).filter(c => (c?.prop?.toLowerCase()?.indexOf('typeid') > -1 && c?.endpoint))) {
      const types = await this.ms.find(col.endpoint, { $skip: 0, $limit: 1000 });
      this.types = types.data;
    }
    this.rows = [];
    if (!this.searchColumns?.length) {
      this.searchColumns = _.filter(this.columns, col => !!col.prop);
    }
    this.searchColumn = this.searchColumns[0].prop;
    this.searchValue = '';

    this.showCustom = this.custom.observers.length > 0;
    this.showDetail = this.detail.observers.length > 0;
    this.showEdit = this.edit.observers.length > 0;
    this.showRemove = this.remove.observers.length > 0;
    this.showAdd = this.add.observers.length > 0;
    this.showCheck = this.check.observers.length > 0;

    // We set the location of a table by its url and for multiple tables on one page we add a table id
    this.location = (this.table_id ? this.router.url + '__' + this.table_id : this.router.url) + this.title;
    const data = await this.storage.getData('data-table-filter-' + this.location);
    if (data) {
      this.skip = data.skip;
      if (_.find(this.columns, obj => {
        return obj.prop === data.searchColumn;
      })) {
        this.searchColumn = data.searchColumn;
      }
      this.searchValue = data.searchValue;
      this.classProps = data.classProps;
      for (let i = 0; i < this.columns.length; i++) {
        data.columns.map(column => {
          if (this.columns[i].prop === column.prop && column.sorted) {
            if (this.columns[i].valueCallback) {
              column.valueCallback = this.columns[i].valueCallback;
            }
            if (this.columns[i].sortField) {
              column.sortField = this.columns[i].sortField;
            }
            this.columns[i] = column;
            this.changeOrder(column, true);
          }
        });
      }
    }
    this.page(this.skip, this.limit);
  }

  ngOnDestroy() {
    if (this.saveFilters) {
      const dataTableFilterStorage = { skip: this.skip, searchColumn: this.searchColumn, searchValue: this.searchValue, columns: this.columns, classProps: this.classProps };
      this.storage.setData('data-table-filter-' + this.location, dataTableFilterStorage);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.searchColumns) {
      this.searchColumns = _.filter(this.columns, col => !!col.prop);
    }
    if (!(changes.validator && !changes.validator.firstChange)) {
      this.ngOnInit();
    }
  }

  public refresh() {
    this.page(this.skip, this.limit);
  }

  public getRowValue(row, column, first = false) {
    let rawValue = row[column.prop];
    if (column.prop.indexOf('.') !== -1) {
      // the target value is an attribute of an object.
      rawValue = row;
      const rawValueParts = column.prop.split('.');
      rawValueParts.map(value => {
        if (rawValue !== null && rawValue !== undefined) {
          rawValue = rawValue[value];
        }
      });
    }

    if (column.valueTransformer) {
      return column.valueTransformer(rawValue);
    }

    if (this.types && column.prop.toLowerCase().indexOf('typeid') > -1 && _.find(this.types, { _id: rawValue })) {
      rawValue = _.find(this.types, { _id: rawValue }).title;
    }

    return rawValue ? rawValue : '&nbsp;';
  }

  public page(skip = this.skip, limit = this.limit) {
    this.isLoading = true;

    let params = this.dataProviderParams;
    const multiple = this.searchColumn === 'all' || this.searchAll;
    if (this.searchValue !== '') {
      const searchParams = {};
      if (this.classProps && this.classProps.api && this.classProps.api.modelType === 'mysql') {
        if (multiple) {
          searchParams['$or'] = this.getSQLSearch(this.searchValue, true);
        } else {
          searchParams[this.searchColumn] = this.getSQLSearch(this.searchValue);
        }
      } else {
        const regexpNumber = new RegExp('^\\d+$');
        if (regexpNumber.test(this.searchValue)) {
          searchParams[this.searchColumn] = {
            $eq: this.searchValue
          }
        } else {
          if (multiple) {
            searchParams['$or'] = this.getMongoSearch(this.searchValue, multiple);
          } else {
            searchParams[this.searchColumn] = this.getMongoSearch(this.searchValue);
          }
        }
      }
      const tempParams = {};
      const and = [];
      if (multiple === true) {
        if (params) {
          for (const key of Object.keys(params)) {
            if (key.startsWith('$')) {
              tempParams[key] = params[key];
            } else {
              const andElem = {};
              andElem[key] = params[key]
              and.push(andElem);
            }
          }
        }
        and.push({ $or: searchParams['$or'] });
        tempParams['$and'] = and;
        params = tempParams;
      } else {
        params = _.merge({}, params, searchParams);
      }
    }

    if (!this.dataProvider && this.plainData) {
      this.rows = this.plainData;
      this.total = this.plainData.length;
      this.skip = skip;
      if (this.searchValue !== '') {
        const data = _.cloneDeep(this.plainData);
        const regex = new RegExp(_.escapeRegExp(this.searchValue), 'gi');
        this.rows = _.filter(data, item => {
          return (regex.test(item[this.searchColumn]));
        });
      }
      this.total = this.rows.length;
      this.rows = this.rows.slice(skip, skip + limit);
      this.navigation.isFirst = (skip === 0);
      this.navigation.isLast = (Math.floor(this.skip / this.limit) === Math.floor(this.total / this.limit));
      let i = 0;
      if (this.validator) {
        this.rows.map(entry => {
          let isValid = this.validator.target && this.validator.target.indexOf(entry[this.validator.validate]) > -1;
          if (this.validator.inverse) {
            isValid = !isValid
          }
          if (isValid) {
            entry.isValidated = true;
            this.selectedIdList.push(i + this.skip);
          }
          i++;
        });
      }
      return;
    }
    return this.dataProvider?.find(skip, limit, params).then(response => {
      this.rows = response.data;
      if (this.rows[0]) {
        try {
          this.classProps = this.rows[0].getReflection().getClassProperties();
          // we need this to toggle mongo and mysql queries
        } catch (e) { /* error occurs if the model has no appropriate data */ }
      }

      this.total = response.total;
      this.skip = response.skip;
      if (this.total < this.skip) {
        this.skip = 0;
        this.page(this.skip);
      }
      this.limit = response.limit;
      this.navigation.isFirst = (this.skip === 0);
      this.navigation.isLast = (Math.floor(this.skip / this.limit) === this.getPageCount());

      if (this.validator) {
        this.selectedIdList = [];
        let i = 0;
        this.rows.map(entry => {
          let isValid = this.validator.target && this.validator.target.indexOf(entry[this.validator.validate]) > -1;
          if (this.validator.inverse) {
            isValid = !isValid
          }
          if (isValid) {
            entry.isValidated = true;
            this.selectedIdList.push(i + this.skip);
          }
          i++;
        });
      }
    });
  }

  public getSQLSearch(searchValue: string, multipleFields = false) {
    const fields = [];
    if (!!multipleFields) {
      for (const col of this.searchColumns) {
        if (col.prop !== 'all') {
          const column = {};
          column[col.prop] = { $like: '%' + searchValue + '%' };
          fields.push(column);
        }
      }
      return fields;
    }
    /* multi term query for sql if we have pipe-char */
    let query = <any>{ $like: '%' + searchValue + '%' };
    const searchTerms = searchValue.split('|');
    if (searchTerms && searchTerms.length > 1) {
      query = { $or: [] };
      searchTerms.map((term: string) => {
        if (term && term.length && _.trim(term).length) {
          query.$or.push({ $like: '%' + _.trim(term) + '%' });
        }
      });
    }
    return query;
  }

  public getMongoSearch(searchValue, multiple = false) {
    /* multi term query for mongoDB if we have pipe-char */
    let exp = _.escapeRegExp(searchValue);
    const searchTerms = searchValue.split('|');
    if (searchTerms && searchTerms.length > 1) {
      exp = '';
      searchTerms.map((term: string, i) => {
        if (term && term.length && _.trim(term).length) {
          exp += _.escapeRegExp(term) + (i < searchTerms.length - 1 ? '|' : '');
        }
      });
    }
    if (multiple) {
      const params = [];
      for (const col of this.searchColumns) {
        const element = {};
        element[col.prop] = { $regex: '^.*' + exp + '.*$', $options: 'i' };
        params.push(element);
      }
      return params;
    }
    return { $regex: '^.*' + exp + '.*$', $options: 'i' };
  }

  public getPageCount() {
    let maxPages = Math.floor(this.total / this.limit);
    if (this.total % this.limit === 0) {
      maxPages -= 1;
    }
    if (this.plainData && !this.dataProvider) {
      maxPages = Math.floor(this.plainData.length / this.limit);
      if (this.plainData.length % this.limit === 0) {
        maxPages -= 1;
      }
    }
    return maxPages;
  }

  public onAdd() {
    this.add.emit();
  }

  public executeHeaderButton(button) {
    button.callback();
  }

  public onCustom(index: number, row: Models.IbexBaseModel, event: any): void {
    event?.stopPropagation();
    this.custom.emit({ index, row });
    this.onReload();
  }

  public onCheck(index: number, row: Models.IbexBaseModel, event: any): void {
    event?.stopPropagation();
    this.checkedIdList.includes(index) ? this.checkedIdList.splice(this.checkedIdList.indexOf(index), 1) : this.checkedIdList.push(index);
    this.check.emit({ index, row, checked: event.srcElement.checked });
  }

  public onDetail(index: number, row: Models.IbexBaseModel, event: any): void {
    event?.stopPropagation();
    this.detail.emit({ index, row });
  }

  public onEdit(index: number, row: Models.IbexBaseModel, event: any): void {
    event?.stopPropagation();
    this.edit.emit({ index, row });
  }

  public onRemove(index: number, row: Models.IbexBaseModel | any, event: any): void {
    event?.stopPropagation();
    if ((row?.name || '').startsWith('pref_')) {
      row.name = row.name.split('_').splice(2, row.name.split('_').length - 1).join('_');
    }
    this.remove.emit({ index, row });
  }

  public onSelect(index: number, row: Models.IbexBaseModel, event: any): void {
    event?.stopPropagation();
    this.selectedIdList.includes(index) ? this.selectedIdList.splice(this.selectedIdList.indexOf(index), 1) : this.selectedIdList.push(index);
    this.select.emit({ index, row });
  }

  public onClick(index: number, row: Models.IbexBaseModel, event: any): void {
    if (this.selectable) {
      this.onSelect(index, row, event);
    } else {
      const count = [this.showDetail, this.showEdit, this.showCustom, this.showRemove, this.showCheck].filter(Boolean).length;
      if (count === 1 && !this.showRemove) {
        if (this.showDetail) {
          this.onDetail(index, row, event);
        } else if (this.showEdit) {
          this.onEdit(index, row, event);
        } else if (this.showCustom) {
          this.onCustom(index, row, event);
        } else if (this.showCheck) {
          this.onCheck(index, row, event);
        }
      }
    }
  }

  public onSearchChange(): void {
    window.clearTimeout(this.searchTimeout);
    this.searchTimeout = window.setTimeout(() => {
      this.page(0, this.limit);
    }, 500);
  }

  public onNavigateForwards() {
    this.page(this.skip + this.limit);
  }

  public onNavigateBackwards() {
    this.page(this.skip - this.limit);

  }

  public onNavigateToFirstPage() {
    this.page(0);
  }

  public onNavigateToLastPage() {
    this.page(this.getPageCount() * this.limit);
  }

  public onReload() {
    this.page(this.skip);
  }

  changeOrder(column: any, restore?) {
    if (!this.dataProvider && this.plainData) {
      this.plainOrder = this.plainOrder === 'asc' ? 'desc' : 'asc';
      this.plainData = _.orderBy(
        this.plainData, [item => _.get(item, column.prop) && _.isString(_.get(item, column.prop)) ? _.get(item, column.prop)
          .toLowerCase() : (_.get(item, column.prop) ? _.get(item, column.prop) : 99999999999999999)], [this.plainOrder]);
      this.page();
      return;
    }
    const fieldName = column.sortField || column.prop || column.name;
    let order: number;
    if (!this.dataProviderParams) {
      this.dataProviderParams = {};
    }
    const sort = this.dataProviderParams['$sort'];
    // sets order if filter is set in localStorage
    if (restore) {
      this.dataProviderParams['$sort'] = {};
      this.dataProviderParams['$sort'][fieldName] = column.sorted;
      return;
    }
    if (column.sortField) {
      order = 1;
      if (sort[fieldName] === 1) {
        this.dataProviderParams['$sort'][column.sortField] = -1;
        order = -1;
      } else {
        delete this.dataProviderParams['$sort'][column.sortField];
      }
      for (let i = 0; i < this.columns.length; i++) {
        if (this.columns[i]['sortField'] === fieldName) {
          this.columns[i]['sorted'] = order;
        } else {
          if (this.columns[i]['sorted']) {
            delete this.columns[i]['sorted'];
          }
        }
      }
    } else {
      if (sort && sort[fieldName]) {
        if (sort[fieldName] === 1) {
          this.dataProviderParams['$sort'][fieldName] = -1;
          order = -1;
        } else {
          delete this.dataProviderParams['$sort'][fieldName];
        }
      } else {
        this.dataProviderParams['$sort'] = {};
        this.dataProviderParams['$sort'][fieldName] = 1;
        order = 1;
      }
      for (let i = 0; i < this.columns.length; i++) {
        if (this.columns[i]['prop'] === fieldName) {
          this.columns[i]['sorted'] = order;
        } else {
          if (this.columns[i]['sorted']) {
            delete this.columns[i]['sorted'];
          }
        }
      }
    }
    if (column.sortField && !this.dataProviderParams['$sort'][fieldName]) {
      this.dataProviderParams['$sort'] = {};
      this.dataProviderParams['$sort'][fieldName] = 1;
    }
    this.page();
  }

  resetFilters() {
    this.storage.removeAllData();
    this.searchColumn = this.columns[0].prop;
    this.searchValue = '';
    this.skip = 0;
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i]['sorted']) {
        delete this.columns[i]['sorted'];
      }
    }
    this.dataProviderParams['$sort'] = {};
    this.page(this.skip, this.limit);
  }

  perPageChange(event) {
    this.limit = event.value;
    this.skip = 0;
    this.refresh();
  }

  checkLastActive(row): boolean {
    for (const key of this.lastActiveChip) {
      if (!!row[key] && moment(row[key], 'YYYY/MM/DD - HH:mm').format('x') > (moment(Date.now())).subtract(15, 'minutes').format('x')) {
        return true;
      }
    }
    return false;
  }

  selectItem(row) {
    if (_.find(this.selectedItems, { FrontProfileID: row.FrontProfileID })) {
      this.selectedItems.splice(_.findIndex(this.selectedItems, { FrontProfileID: row.FrontProfileID }), 1);
    } else {
      this.selectedItems.push(_.cloneDeep(row));
    }
  }

  selectAll(event: any) {
    const elements = Array.from(document.querySelectorAll('.checkbox input'));
    for (const element of elements) {
      if (event.target !== element) {
        (<any>element).checked = !!event.target.checked;
      }
    }
    if (event.target.checked) {
      this.selectedItems = _.cloneDeep(this.rows);
    } else {
      this.selectedItems = [];
    }
  }

  checkAll(event: any) {
    const elements = document.querySelectorAll('.check-checkbox');
    elements.forEach((el: any) => {
      if (el.checked === event.srcElement.checked) {
        return;
      }
      el.checked = event.srcElement.checked;
      const index = parseInt(el.id.replace(/\D/gmi, ''), 10);
      this.onCheck(index, this.rows[index], event);
    });
  }

  public unselectAll() {
    const elements = Array.from(document.querySelectorAll('.checkbox input'));
    for (const element of elements) {
      (<any>element).checked = false;
    }
    this.selectedItems = [];
  }

  executeSelectedAction(action) {
    action.callback(action.idField ? _.map(this.selectedItems, action.idField) : this.selectedItems);
  }

  isChecked(element) {
    return !!_.find(this.selectedItems, { 'FrontProfileID': element.FrontProfileID });
  }
}
